import { useState, useCallback } from 'react'

/**
 * Hook to sync state with localStorage
 * @param key - localStorage key
 * @param initialValue - Initial value if nothing in localStorage
 */
export function useLocalStorage<T>( key: string, initialValue: T ): [ T, ( value: T ) => void ] {
	const [ storedValue, setStoredValue ] = useState<T>( () => {
		try {
			const item = localStorage.getItem( key )
			return item ? JSON.parse( item ) : initialValue
		} catch ( error ) {
			// eslint-disable-next-line no-console
			console.warn( `Failed to load "${key}" from localStorage:`, error )
			return initialValue
		}
	} )

	const setValue = useCallback( ( value: T ) => {
		try {
			setStoredValue( value )
			if ( value === null || value === undefined ) {
				localStorage.removeItem( key )
			} else {
				localStorage.setItem( key, JSON.stringify( value ) )
			}
		} catch ( error ) {
			// eslint-disable-next-line no-console
			console.warn( `Failed to save "${key}" to localStorage:`, error )
			// Still update state even if localStorage fails
		}
	}, [ key ] )

	return [ storedValue, setValue ]
}
